import type { CallResult, ProfileStats } from '@/types'

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0
  const idx = Math.ceil((p / 100) * sorted.length) - 1
  return sorted[Math.max(0, idx)]
}

function median(sorted: number[]): number {
  return percentile(sorted, 50)
}

function avg(values: number[]): number {
  if (values.length === 0) return 0
  return values.reduce((a, b) => a + b, 0) / values.length
}

export function computeProfileStats(
  profileId: string,
  profileName: string,
  profileType: CallResult['profileType'],
  results: CallResult[]
): ProfileStats {
  const successful = results.filter((r) => r.success)
  const times = successful.map((r) => r.totalTimeSec).sort((a, b) => a - b)
  const ttfts = successful.map((r) => r.ttftSec).sort((a, b) => a - b)
  const throughputs = successful
    .filter((r) => r.tokenThroughput > 0)
    .map((r) => r.tokenThroughput)
    .sort((a, b) => a - b)

  const brInv = successful.map((r) => r.bedrockInvocationLatencyMs).filter((v) => v > 0)
  const brFtb = successful.map((r) => r.bedrockFirstByteLatencyMs).filter((v) => v > 0)
  const grIn = successful.map((r) => r.guardrailInputLatencyMs).filter((v) => v > 0)
  const grOut = successful.map((r) => r.guardrailOutputLatencyMs).filter((v) => v > 0)
  const grTot = successful.map((r) => r.guardrailTotalLatencyMs).filter((v) => v > 0)
  const e2eTotals = successful.map((r) => r.e2eTotalSec ?? 0).filter((v) => v > 0)
  const e2eOverheads = successful.map((r) => r.e2eOverheadMs ?? 0).filter((v) => v > 0)

  return {
    profileId,
    profileName,
    profileType,
    callCount: results.length,
    successCount: successful.length,
    failureCount: results.length - successful.length,
    avgTotalSec: avg(times),
    medianTotalSec: median(times),
    p75TotalSec: percentile(times, 75),
    p90TotalSec: percentile(times, 90),
    p95TotalSec: percentile(times, 95),
    p99TotalSec: percentile(times, 99),
    minTotalSec: times[0] ?? 0,
    maxTotalSec: times[times.length - 1] ?? 0,
    avgTtftSec: avg(ttfts),
    medianTtftSec: median(ttfts),
    p75TtftSec: percentile(ttfts, 75),
    p90TtftSec: percentile(ttfts, 90),
    p95TtftSec: percentile(ttfts, 95),
    avgInputTokens: avg(successful.map((r) => r.inputTokens)),
    avgOutputTokens: avg(successful.map((r) => r.outputTokens)),
    avgThroughput: avg(throughputs),
    medianThroughput: median(throughputs),
    avgBedrockInvocationMs: avg(brInv),
    avgBedrockFirstByteMs: avg(brFtb),
    avgGuardrailInputMs: avg(grIn),
    avgGuardrailOutputMs: avg(grOut),
    avgGuardrailTotalMs: avg(grTot),
    avgE2eTotalSec: avg(e2eTotals),
    avgE2eOverheadMs: avg(e2eOverheads)
  }
}

/**
 * Compute ProfileStats on-the-fly from a BenchmarkRun's raw results.
 * Use this instead of accessing run.stats directly — stats are no longer persisted.
 */
export function getRunStats(run: { results: CallResult[]; profiles?: { id: string; name: string }[] }): ProfileStats[] {
  // Group results by profileId
  const byProfile = new Map<string, CallResult[]>()
  for (const r of run.results) {
    const list = byProfile.get(r.profileId) ?? []
    list.push(r)
    byProfile.set(r.profileId, list)
  }

  return Array.from(byProfile.entries()).map(([profileId, results]) => {
    const sample = results[0]
    const profileName = sample.profileName
    const profileType = sample.profileType
    return computeProfileStats(profileId, profileName, profileType, results)
  })
}
